import React, { useState } from "react";
import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import "./Message.scss";
import moment from "moment";
// import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import usePermissions from "hooks/usePermissions";
import { DateTimeFormat } from "helpers/dateTimeHelper";
import Badge from "../../../../../../../components/atoms/Badge/Badge";

/**
 * Formats the date of the message depending on when the message was sent
 * @returns a tooltip with a formatted date of the message
 */
const formatMessageDate = (date: string) => {
  const messageDate = moment(date);
  let format = DateTimeFormat.fullDateTimeFormat;
  if (messageDate.isSame(new Date(), "day")) format = DateTimeFormat.timeFormat;
  if (messageDate.isSame(new Date(), "year")) format = DateTimeFormat.shortDateTimeFormat;
  return messageDate.format(format);
};

/**
 * Get the initials of the provided string
 * @param name the string to get the initials of
 * @returns the initials of the provided string
 */
const getInitials = (name: string) => {
  const nameArray = name.split(" ");
  const initials = nameArray.map((n) => n.charAt(0)).join("");
  return initials;
};

/**
 * Renders the logo of the company or the initials of the author
 * @param companyLogo the url of the company logo
 * @param authorName the name of the author
 * @returns either the logo of the company or the initials of the author
 */
const renderLogo = (companyLogo: string, authorName: string) => {
  if (companyLogo) return <img src={companyLogo} alt="Logo" width="50px" style={{ objectFit: "contain" }} />;
  return (
    <div
      className="d-flex pr-0 align-items-center justify-content-center text-white font-b1"
      style={{ height: "48px", width: "48px", backgroundColor: "#0071ce", borderRadius: "5px" }}
    >
      {getInitials(authorName)}
    </div>
  );
};

/**
 * Gets the uploaded files of the message
 * @param data the uploaded data of the message
 * @returns a list of <a> tags for each file in the delivery
 */
const getDeliveryFiles = (data: any) => {
  const files: any = [];
  let currentFile: any = null;
  data.forEach((item: any) => {
    if (item.key === "attachment_url") {
      currentFile = {
        url: item.value,
      };
    } else if (item.key === "attachment_name") {
      if (currentFile) {
        const parts = item.value.split("/");
        currentFile.name = parts[parts.length - 1]; // Extract the filename from the path
        files.push(currentFile);
        currentFile = null;
      }
    }
  });
  return files.map((file: any) => `<a href="${file.url}" target="_blank">${file.name}</a>`).join(", ");
};

/**
 * Formats the message depending on the type of message
 * - delivery: formats the delivery date, delivery acceptor and delivery files
 * - upload: formats the message to include the uploaded file
 * - for all types: formats the message to replace new lines with <br> and replace urls with <a href="url">url</a>
 * @param originalMessage the original message string
 * @param data the data (uploaded files) of the message
 * @param type the type of the message; service_change, delivered, upload, schedule_change, webhook, auto_update, general
 * @returns the formatted message string
 */
const formatMessage = (originalMessage: string, data: any, type: any) => {
  let formattedMessage = originalMessage;

  if (type === "delivered") {
    // format the delivery date, delivery acceptor and delivery files
    const unformattedDeliveryDate = data.find((item: any) => item.key === "time_of_delivery")?.value;
    const deliveryDate = moment(unformattedDeliveryDate).utcOffset("-0000").format(DateTimeFormat.fullDateTimeFormat);
    const deliveryAcceptor = data.find((item: any) => item.key === "pod_name")?.value;
    const deliveryFiles = getDeliveryFiles(data);

    // set the new formatted message
    formattedMessage = `on ${deliveryDate}, acceptor: ${deliveryAcceptor} </br> Files: ${deliveryFiles}`;
  } else if (type === "upload" && data.find((item: any) => item.key === "attachment_url")?.value) {
    // if the message is an upload, format the message to include the uploaded file
    const attachmentUrl = data.find((item: any) => item.key === "attachment_url").value;
    const attachmentNameSplit = data.find((item: any) => item.key === "attachment_name").value.split("/");
    const attachmentName = attachmentNameSplit[attachmentNameSplit.length - 1];

    // set the new formatted message
    formattedMessage = `${
      originalMessage ? `${originalMessage} </br>` : ""
    } File: <a href="${attachmentUrl}" target="_blank">${attachmentName}</a>`;
  }

  return formattedMessage;
};

type MessageProps = {
  message: any;
  // openDeleteMessageDialog: Function;
  isSubsequentMessage?: boolean;
  isFirstMessage: boolean;
};

/**
 * A message component which is used to display a message in the Chat component.
 */
const Message: React.FC<MessageProps> = function ({
  message,
  // openDeleteMessageDialog,
  isSubsequentMessage,
  isFirstMessage,
}) {
  const intl = useIntl();
  if (!message) return null;

  const { permissions } = usePermissions();
  // const { email } = JSON.parse(permissions || "{}");
  // const { author_email, author_name, company, type, timestamp, count, tracking_key, data } = message;
  const { author_name, company, type, timestamp, count, data, attachment_deleted, deleted_attachments } = message;

  const companyLogo = company?.company_logo;
  const companyName = company?.company_name;
  const formattedMessage = formatMessage(message.message, data, type);
  const formattedDate = formatMessageDate(timestamp);
  // const isCurrentUserAuthorOfComment = email === author_email;
  const renderAsSubsequentMessage = isSubsequentMessage && type !== "delivered";

  const isCustomBroker =
    JSON.parse(permissions || "{}")?.custom_broker_teams?.teams?.some(
      (broker: { label: string }) => broker.label === companyName,
    ) || false;

  // Used to display the remove button when the user hovers over the message
  const [isHovered, setIsHovered] = useState<Boolean>(false);

  return (
    <div className={!renderAsSubsequentMessage && !isFirstMessage ? "message-separator" : "subsequent-message-separator"}>
      <div
        className="d-flex flex-row message-container"
        style={{
          marginTop: renderAsSubsequentMessage ? -15 : 0,
          paddingTop: renderAsSubsequentMessage ? 0 : 10,
          marginLeft: renderAsSubsequentMessage ? 1.5 : 0,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Message logo */}
        <div style={{ width: 25, height: 25 }} className="d-flex justify-content-center mt-1">
          {!renderAsSubsequentMessage && renderLogo(companyLogo, author_name)}
        </div>
        {/* Message header: name, company & date */}
        <div className="d-flex flex-column justify-content-between w-100" style={{ marginLeft: 8 }}>
          {!renderAsSubsequentMessage && (
            <div className="d-flex align-items-center">
              <p className="font-b4">
                {companyName && `${companyName} | `}
                {author_name}
              </p>
              {isCustomBroker && (
                <div
                  style={{
                    backgroundColor: "#E9ECEE",
                    color: "#7E8589",
                    padding: "2px 5px",
                    borderRadius: 10,
                    fontSize: 10,
                    marginLeft: 5,
                  }}
                >
                  Customs broker
                </div>
              )}

              <p className="text-secondary font-b3 ml-2">
                <span style={{ cursor: "default" }}>{formattedDate}</span>
              </p>
            </div>
          )}
          {/* Message content */}
          <div className="d-flex position-relative">
            {/* Subsequent message date; appears on hover */}
            {renderAsSubsequentMessage && isHovered && (
              <span className="text-secondary font-b3 subsequent-message-date">{moment(timestamp).format("HH:mm")}</span>
            )}
            <div className="w-100 markdown-list-style font-b2" style={{ lineHeight: "20px" }}>
              {type === "delivered" && (
                <div className="d-flex mt-1" style={{ fontSize: 11 }}>
                  <Badge
                    type="primary"
                    label={intl.formatMessage({
                      id: "general.delivered",
                      defaultMessage: "Delivered",
                    })}
                  />
                </div>
              )}
              <ReactMarkdown linkTarget="_blank" rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                {formattedMessage}
              </ReactMarkdown>
              {(attachment_deleted || deleted_attachments) && (
                <div className="text-disabled">
                  {"<"}
                  <FormattedMessage
                    id="tracking.attachmentDeleted"
                    defaultMessage={`Attachment deleted by ${author_name}`}
                    values={{ authorName: author_name }}
                  />
                  {">"}
                </div>
              )}
            </div>
            {/* Message duplicate counter */}
            {count && count > 1 && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="freight-mode">
                    <span className="font-weight-normal">
                      This exact message has been posted {count} times. Previous duplicates have been hidden.
                    </span>
                  </Tooltip>
                }
              >
                <p className="text-secondary ml-4">{count}x</p>
              </OverlayTrigger>
            )}
            {/* Message delete button */}
            {/* <div>
              {isCurrentUserAuthorOfComment && openDeleteMessageDialog && isHovered && (
                <div>
                  <CircleCross
                    stroke="black"
                    style={{ cursor: "pointer", marginLeft: 6 }}
                    onClick={() => openDeleteMessageDialog(tracking_key)}
                    height={16}
                    width={16}
                  />
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

Message.propTypes = {
  message: PropTypes.instanceOf(Object).isRequired,
  // openDeleteMessageDialog: PropTypes.func.isRequired,
  isSubsequentMessage: PropTypes.bool.isRequired,
};

export default Message;
